import { getBodyAgeGroup } from '#assets/images/gutXAxis';
import { GutXAxisBody } from '#components/GutXAxisBody';
import type { GutXAxisOrgan, OrganLookupCode } from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import c from 'classnames';
import styles from './GutXAxisOrganBadge.module.css';

export type GutXAxisOrganBadgeProps = {
  organ: GutXAxisOrgan;
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /**
   * Every code the map draws: a badge that knew only its own organ would crop a
   * gut the map never painted.
   */
  drawnCodes?: ReadonlySet<OrganLookupCode>;
  /** Lands on the circle, e.g. to resize it where the PDF headings crop smaller. */
  className?: string;
};

/**
 * An organ as a circular crop of the same body the map draws, so the two can
 * never disagree about a kit's artwork or its colour.
 */
export function GutXAxisOrganBadge({
  organ,
  age,
  pregnant,
  sex,
  drawnCodes,
  className,
}: GutXAxisOrganBadgeProps) {
  // Only an adult's intestines make room for a baby, so only they crop differently.
  const withBaby = pregnant && getAgeGroup(age) === AgeGroup.ADULT;

  return (
    <span className={c(styles.badge, styles[organ.flag_level], className)}>
      <GutXAxisBody
        className={c(
          styles.stage,
          styles[organ.lookup_code],
          styles[getBodyAgeGroup({ age, pregnant })],
          withBaby && styles.pregnant,
        )}
        organs={[organ]}
        age={age}
        pregnant={pregnant}
        sex={sex}
        drawnCodes={drawnCodes}
        waitForImages={false}
      />
    </span>
  );
}

export default GutXAxisOrganBadge;
